import React, { useState, useRef } from 'react';
import { exportElementToPdf } from '../../lib/pdfExport';
import { generateAgreementMarkdown } from '../../lib/toolCalculations';

interface AgreementArmorProps {
  isUnlocked?: boolean;
  onOpenCheckout?: (tier?: 'single' | 'bundle') => void;
}

export const AgreementArmor: React.FC<AgreementArmorProps> = ({
  isUnlocked = false,
  onOpenCheckout,
}) => {
  const documentRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [contractorName, setContractorName] = useState('Vance Systems Architecture LLC');
  const [contractorTaxId, setContractorTaxId] = useState('EIN: 84-2910482');
  const [clientEntity, setClientEntity] = useState('Beacon Logistics Corp');
  const [legalJurisdiction, setLegalJurisdiction] = useState('State of Delaware, USA');
  const [modelType, setModelType] = useState<'sprint' | 'retainer'>('sprint');
  const [feeAmount, setFeeAmount] = useState(8500);
  const [turnaroundHours, setTurnaroundHours] = useState(72);
  const [graceDays, setGraceDays] = useState(5);
  const [warrantyDays, setWarrantyDays] = useState(7);
  const [copied, setCopied] = useState(false);
  const isWatermarked = !isUnlocked;

  const handleExportPdf = async () => {
    try {
      setIsExporting(true);
      const sanitizedClient = clientEntity.replace(/[^a-zA-Z0-9_-]/g, '-').replace(/-+/g, '-').slice(0, 24);
      await exportElementToPdf(documentRef.current, {
        filename: `Master-Services-Agreement-${sanitizedClient || 'Client'}-AGMT-2026-MSA-049.pdf`,
        documentTitle: `Master Services Agreement · ${clientEntity}`,
        isUnlocked,
      });
    } catch (err) {
      console.error('Agreement PDF export failed:', err);
      window.print();
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyMarkdown = () => {
    const md = generateAgreementMarkdown({
      contractorName,
      contractorTaxId,
      clientEntity,
      legalJurisdiction,
      modelType,
      feeAmount,
      turnaroundHours,
      graceDays,
      warrantyDays,
      isWatermarked,
    });

    navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8">
      {/* Top Banner (Hidden in PDF / Print) */}
      <div className="no-print screen-only flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#232736] pb-6">
        <div>
          <div className="text-[11px] font-mono text-sky-400 uppercase tracking-widest flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
            TOOL 04 // AGREEMENT ARMOR &amp; PLAIN-ENGLISH SOW
          </div>
          <h2 className="font-tech text-3xl font-bold text-white mt-1">
            Plain-English SOW &amp; Retainer SLA Builder
          </h2>
          <p className="text-xs text-[#8b92a5] font-mono mt-1">
            Generate airtight 2-page service agreements with non-negotiable legal protections against ghosting and scope drift.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {isUnlocked ? (
            <span className="px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] font-bold flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              PRO UNLOCKED
            </span>
          ) : (
            <button
              onClick={() => onOpenCheckout?.('single')}
              className="px-2.5 py-1.5 rounded bg-sky-400 hover:bg-sky-300 text-black font-mono text-[10px] font-bold transition flex items-center gap-1.5 shadow-[0_0_10px_rgba(56,189,248,0.2)]"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
              UNLOCK PRO ($9)
            </button>
          )}
          <button
            onClick={handleCopyMarkdown}
            className="px-3 py-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-xs font-mono text-white rounded transition flex items-center gap-1.5"
          >
            {copied ? '✓ Copied Contract' : 'Copy Contract MD'}
          </button>
          <button
            onClick={handleExportPdf}
            disabled={isExporting}
            className="px-3.5 py-1.5 bg-sky-400 hover:bg-sky-300 disabled:opacity-50 text-black font-tech font-bold text-xs rounded transition flex items-center gap-1.5 shadow-[0_0_15px_rgba(56,189,248,0.25)]"
          >
            {isExporting ? (
              <>
                <span className="w-2 h-2 rounded-full border-2 border-black border-t-transparent animate-spin" />
                Compiling PDF...
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {isUnlocked ? 'Download Vector Agreement PDF' : 'Download Agreement PDF (Free)'}
              </>
            )}
          </button>
          <button
            onClick={() => window.print()}
            className="p-1.5 bg-[#181b26] border border-[#232736] hover:border-sky-500/40 text-[#8b92a5] hover:text-white rounded transition"
            title="Print via OS Dialog"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Input Column */}
        <div className="lg:col-span-5 space-y-5 no-print">
          <div className="blueprint-card rounded-xl p-4 border border-[#232736] flex items-center justify-between font-mono text-xs">
            <span className="text-[11px] text-[#8b92a5] uppercase">Vector PDF Status:</span>
            {isUnlocked ? (
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold">
                Pro Unbranded (Active)
              </span>
            ) : (
              <button
                onClick={() => onOpenCheckout?.('single')}
                className="text-[10px] px-2 py-0.5 rounded bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 transition font-bold"
              >
                Watermarked · Unlock Pro ($9)
              </button>
            )}
          </div>
          <div className="blueprint-card rounded-xl p-5 border border-[#232736] space-y-4">
            <h3 className="font-tech text-base font-bold text-white border-b border-[#232736] pb-2">
              Contract Parties & Clauses
            </h3>

            <div className="space-y-3 font-mono text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Your Studio Entity
                  </label>
                  <input
                    type="text"
                    value={contractorName}
                    onChange={(e) => setContractorName(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Client Entity
                  </label>
                  <input
                    type="text"
                    value={clientEntity}
                    onChange={(e) => setClientEntity(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Vendor Tax ID / EIN
                  </label>
                  <input
                    type="text"
                    value={contractorTaxId}
                    onChange={(e) => setContractorTaxId(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Governing Jurisdiction
                  </label>
                  <input
                    type="text"
                    value={legalJurisdiction}
                    onChange={(e) => setLegalJurisdiction(e.target.value)}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400 text-[11px]"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                  Engagement Model
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setModelType('sprint')}
                    className={`py-2 rounded text-xs font-mono border ${
                      modelType === 'sprint'
                        ? 'bg-sky-500/20 border-sky-400 text-sky-300 font-bold'
                        : 'bg-[#10121a] border-[#232736] text-zinc-400'
                    }`}
                  >
                    Fixed-Scope Sprint
                  </button>
                  <button
                    onClick={() => setModelType('retainer')}
                    className={`py-2 rounded text-xs font-mono border ${
                      modelType === 'retainer'
                        ? 'bg-sky-500/20 border-sky-400 text-sky-300 font-bold'
                        : 'bg-[#10121a] border-[#232736] text-zinc-400'
                    }`}
                  >
                    Async Retainer Queue
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 border-t border-[#232736] pt-3">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Agreed Fee ($)
                  </label>
                  <input
                    type="number"
                    value={feeAmount}
                    onChange={(e) => setFeeAmount(Number(e.target.value))}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Turnaround SLA
                  </label>
                  <select
                    value={turnaroundHours}
                    onChange={(e) => setTurnaroundHours(Number(e.target.value))}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  >
                    <option value={48}>48 Hours</option>
                    <option value={72}>72 Hours</option>
                    <option value={96}>96 Hours</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Late Grace Days
                  </label>
                  <input
                    type="number"
                    value={graceDays}
                    onChange={(e) => setGraceDays(Number(e.target.value))}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#8b92a5] uppercase block mb-1">
                    Warranty Days
                  </label>
                  <input
                    type="number"
                    value={warrantyDays}
                    onChange={(e) => setWarrantyDays(Number(e.target.value))}
                    className="w-full px-2.5 py-2 rounded bg-[#10121a] border border-[#232736] text-white focus:outline-none focus:border-sky-400"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-[#232736] pt-3">
                <span className="text-[11px] text-[#8b92a5] uppercase">Watermark:</span>
                {isUnlocked ? (
                  <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold">
                    Pro (Removed)
                  </span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded font-mono bg-zinc-800 text-zinc-400">
                    Watermarked (Free)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Live Contract Document Preview */}
        <div className="lg:col-span-7">
          <div ref={documentRef} className="print-content blueprint-card rounded-2xl p-6 sm:p-8 border border-sky-500/30 space-y-6 bg-[#0c0e14]">
            {/* PAGE 1: PREAMBLE & ARTICLES 1 - 3 */}
            <div className="pdf-page space-y-5">
              {/* Executive Document Header & Letterhead */}
              <div className="print-doc-header border-b border-[#232736] pb-5 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <span className="text-[10px] font-mono text-sky-400 uppercase tracking-widest font-bold block">
                      LEGAL INSTRUMENT // PLAIN-ENGLISH MASTER SERVICES AGREEMENT
                    </span>
                    <h3 className="font-tech text-2xl sm:text-3xl font-bold text-white mt-0.5">
                      Master Services Agreement &amp; SOW
                    </h3>
                  </div>
                  <div className="text-right font-mono text-xs">
                    <span className="text-[10px] text-[#8b92a5] uppercase block">DOCUMENT REF</span>
                    <span className="text-white font-bold tracking-wider">AGMT-2026-MSA-049</span>
                  </div>
                </div>

                {/* Metadata Bar */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-[#232736] text-[10px] font-mono text-[#8b92a5]">
                  <div>
                    <span className="block text-zinc-500 uppercase">Effective Date:</span>
                    <span className="text-zinc-300 font-bold">{new Date().toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="block text-zinc-500 uppercase">Model:</span>
                    <span className="text-zinc-300 font-bold">
                      {modelType === 'sprint' ? 'Dedicated Sprint' : 'Async Retainer'}
                    </span>
                  </div>
                  <div>
                    <span className="block text-zinc-500 uppercase">Payment Terms:</span>
                    <span className="text-zinc-300 font-bold">Net-15 Strict</span>
                  </div>
                  <div>
                    <span className="block text-zinc-500 uppercase">Jurisdiction:</span>
                    <span className="text-sky-400 font-bold">{legalJurisdiction}</span>
                  </div>
                </div>

                {/* Parties Summary Box */}
                <div className="p-3.5 rounded-xl bg-[#10121a] border border-[#232736] grid grid-cols-2 gap-4 text-xs font-mono">
                  <div>
                    <span className="text-[10px] text-zinc-500 uppercase block font-bold">CLIENT ENTITY:</span>
                    <span className="text-white font-bold block">{clientEntity}</span>
                    <span className="text-[11px] text-[#8b92a5]">Authorized Project Sponsor</span>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] text-zinc-500 uppercase block font-bold">SERVICE PROVIDER:</span>
                    <span className="text-white font-bold block">{contractorName}</span>
                    <span className="text-[10px] text-zinc-400 block">{contractorTaxId}</span>
                    <span className="text-[11px] text-sky-400">${feeAmount.toLocaleString()} USD Total Consideration</span>
                  </div>
                </div>
              </div>

              {/* Preamble & Recitals */}
              <div className="p-3.5 rounded-xl bg-[#10121a] border border-[#232736] space-y-1.5 font-sans text-xs text-zinc-300 leading-relaxed">
                <span className="text-[10px] font-mono text-sky-400 font-bold uppercase tracking-wider block">
                  PREAMBLE &amp; RECITALS
                </span>
                <p>
                  This Master Services Agreement (the &quot;Agreement&quot;) is entered into as of <strong className="text-white font-mono">{new Date().toLocaleDateString()}</strong> by and between <strong className="text-white">{contractorName}</strong> (&quot;Provider&quot;, {contractorTaxId}) and <strong className="text-white">{clientEntity}</strong> (&quot;Client&quot;).
                </p>
                <p className="text-zinc-400 text-[11px]">
                  WHEREAS, Client desires to retain Provider to perform professional systems architecture, software engineering, and technical advisory services; and WHEREAS, Provider possesses the specialized expertise, dedicated capacity, and qualifications to deliver such services; NOW, THEREFORE, in consideration of the mutual covenants set forth herein, the parties agree as follows:
                </p>
              </div>

              {/* Core Legal Articles: 1 to 3 */}
              <div className="space-y-4">
                <div className="print-card print-avoid-break space-y-1">
                  <h4 className="text-white font-mono font-bold uppercase text-[11px]">
                    1. Engagement Scope &amp; Production Capacity
                  </h4>
                  <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                    {modelType === 'sprint'
                      ? `The Provider reserves dedicated calendar production capacity exclusively for Client for a locked sprint. Deliverables are strictly limited to Exhibit A specifications. Any delays in Client asset provisioning, third-party API credentials, or milestone reviews shall shift delivery dates day-for-day without extending Provider's calendar commitment.`
                      : `The Provider agrees to maintain an active Asynchronous Task Queue for Client. Standard deliverables and code reviews shall be processed within ${turnaroundHours} business hours. Client may populate an unlimited backlog, but exactly ONE (1) ticket shall be in active production at any given moment.`}
                  </p>
                </div>

                <div className="print-card print-avoid-break space-y-1">
                  <h4 className="text-white font-mono font-bold uppercase text-[11px]">
                    2. Asynchronous Communication Mandate &amp; Velocity
                  </h4>
                  <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                    To maximize engineering velocity, all status reporting, backlog grooming, and deliverable reviews shall be conducted asynchronously via project tracking boards and high-resolution video walkthroughs. Synchronous meetings (Zoom, Google Meet, phone calls) are expressly excluded from base fees and must be booked separately at Provider's prevailing advisory rate ($350/hr).
                  </p>
                </div>

                <div className="print-card print-avoid-break space-y-1.5 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <h4 className="text-red-400 font-mono font-bold uppercase text-[11px]">
                    3. Invoicing, Net-15 Settlement &amp; Mandatory Production Freeze
                  </h4>
                  <p className="font-sans text-xs text-zinc-300 leading-relaxed">
                    Invoices are payable strictly Net-15 from date of issuance. If an invoice crosses the {graceDays}-day grace threshold without confirmed settlement, active software development, staging branch deployments, production releases, and queue processing shall immediately and automatically pause without penalty until all outstanding balances are settled in full.
                  </p>
                </div>
              </div>
            </div>

            {/* Screen-Only Pagination Divider */}
            <div className="no-print screen-only py-2 flex items-center gap-3">
              <div className="h-px bg-sky-500/20 flex-1" />
              <span className="text-[10px] font-mono text-sky-400/70 uppercase tracking-widest px-2.5 py-0.5 rounded border border-sky-500/20 bg-sky-500/5">
                PAGE BREAK // PAGE 2 COVENANTS &amp; SIGNATURES
              </span>
              <div className="h-px bg-sky-500/20 flex-1" />
            </div>

            {/* PAGE 2: ARTICLES 4 - 8, SIGNATURES & CLOSING */}
            <div className="pdf-page space-y-4 pt-2">
              {/* Running Header on Page 2 */}
              <div className="border-b border-[#232736] pb-2 flex items-center justify-between text-[10px] font-mono text-[#8b92a5]">
                <span className="font-bold text-sky-400 uppercase tracking-wider">
                  AGREEMENT ARMOR // SECTION B: COVENANTS, INTEGRATION &amp; EXECUTION
                </span>
                <span className="text-zinc-400 font-mono">
                  REF: AGMT-2026-MSA-049 · PAGE 2 OF 2
                </span>
              </div>

              {/* Core Legal Articles: 4 to 8 */}
              <div className="space-y-3.5">
                <div className="print-card print-avoid-break space-y-1">
                  <h4 className="text-white font-mono font-bold uppercase text-[11px]">
                    4. {modelType === 'sprint' ? 'Post-Launch Defect Warranty Window' : 'Strict Zero-Rollover Capacity Policy'}
                  </h4>
                  <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                    {modelType === 'sprint'
                      ? `Provider warrants all custom software delivered under Exhibit A for a period of ${warrantyDays} calendar days following staging cutover solely to remediate functional defects or regressions originating from Exhibit A deliverables. New feature requests, scope expansions, or third-party API changes during this window require an executed Change Order.`
                      : `Retainer capacity represents reserved availability on Provider's production calendar for each 30-day billing cycle. Unused days, hours, or tasks do not accumulate, roll over to subsequent months, or convert into financial credits or refunds under any circumstances.`}
                  </p>
                </div>

                <div className="print-card print-avoid-break space-y-1">
                  <h4 className="text-white font-mono font-bold uppercase text-[11px]">
                    5. Conditional Intellectual Property Conveyance
                  </h4>
                  <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                    Full title, ownership, and copyright of custom source code and technical deliverables produced under this Agreement transfer irrevocably to Client solely upon confirmed receipt of 100% of agreed fees (${feeAmount.toLocaleString()} USD). Prior to complete settlement, Provider retains exclusive equitable title, and Client is granted a temporary revocable evaluation license.
                  </p>
                </div>

                <div className="print-card print-avoid-break space-y-1">
                  <h4 className="text-white font-mono font-bold uppercase text-[11px]">
                    6. Mutual Confidentiality &amp; Data Security
                  </h4>
                  <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                    Both parties agree to treat all proprietary source code, credentials, architecture diagrams, customer data, and commercial terms with a strict commercial standard of care. Neither party shall disclose Confidential Information to third parties without prior written consent.
                  </p>
                </div>

                <div className="print-card print-avoid-break space-y-1">
                  <h4 className="text-white font-mono font-bold uppercase text-[11px]">
                    7. Limitation of Liability &amp; Governing Jurisdiction
                  </h4>
                  <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                    To the maximum extent permitted by applicable law, Provider's aggregate liability arising under this Agreement, whether in contract, tort, or otherwise, shall not exceed the total fees actually received by Provider from Client hereunder. This Agreement shall be construed and governed in accordance with the laws of the {legalJurisdiction}.
                  </p>
                </div>

                <div className="print-card print-avoid-break space-y-1">
                  <h4 className="text-white font-mono font-bold uppercase text-[11px]">
                    8. Severability, Modification &amp; Entire Agreement (Integration Rider)
                  </h4>
                  <p className="font-sans text-xs text-zinc-400 leading-relaxed">
                    This Agreement, together with Exhibit A and any mutually executed Change Orders, constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior negotiations, representations, warranties, or agreements, whether written or oral. No amendment, modification, or waiver of any provision of this Agreement shall be effective unless in writing and executed by authorized representatives of both parties. If any provision is adjudicated invalid or unenforceable, such provision shall be severed and the remaining covenants shall continue in full force and effect.
                  </p>
                </div>

                {/* Signature Blocks */}
                <div className="print-signature-block print-avoid-break grid grid-cols-2 gap-6 pt-4 border-t border-[#232736]">
                  <div className="space-y-2">
                    <span className="text-[10px] font-mono text-zinc-500 uppercase block font-bold">CLIENT ENTITY:</span>
                    <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                      {clientEntity}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 space-y-0.5">
                      <div>SIGNATURE: ___________________________</div>
                      <div>PRINTED NAME: ___________________________</div>
                      <div>TITLE: Authorized Corporate Signatory</div>
                      <div>DATE: ___________________________</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="text-[10px] font-mono text-zinc-500 uppercase block font-bold">SERVICE PROVIDER:</span>
                    <div className="border-b border-zinc-600 pb-1 text-white font-tech font-bold min-h-[22px]">
                      {contractorName}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 space-y-0.5">
                      <div>SIGNATURE: ___________________________</div>
                      <div>PRINTED NAME: ___________________________</div>
                      <div>TITLE: Principal Systems Architect</div>
                      <div>DATE: {new Date().toLocaleDateString()}</div>
                    </div>
                  </div>
                </div>
              </div>

              {isWatermarked && (
                <div className="border-t border-[#232736] pt-3 text-right text-[10px] font-mono text-sky-400/80">
                  Generated with Bureau // Agreement Armor · Pro Unlocks Unbranded Vector PDF Export
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
